import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import useCountries from "@/app/hooks/useCountries";
import { toast } from "react-hot-toast";

export type CountrySelectValue = {
  label: string;
  latlng: number[];
  region: string;
  value: string;
};

interface CountrySelectProps {
  value?: CountrySelectValue | null;
  onChange: (value: CountrySelectValue | null) => void;
  disabled?: boolean;
}

const CountrySelect: React.FC<CountrySelectProps> = ({ value, onChange, disabled }) => {
  const { getAll } = useCountries();
  const [countries, setCountries] = React.useState<CountrySelectValue[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchCountries = async () => {
      try {
        const allCountries = await getAll();
        const sortedCountries = allCountries.sort((a, b) =>
          a.label.localeCompare(b.label)
        );
        setCountries(sortedCountries);
      } catch (error) {
        toast.error("Failed to load countries.");
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, [getAll]);

  const filteredCountries = React.useMemo(() => 
    countries.filter((country) =>
      country.label.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [countries, searchQuery]
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
    }
  };

  const handleSearchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const handleSelect = (newValue: string) => {
    const selectedCountry = countries.find(
      (country) => country.value === newValue
    );
    onChange(selectedCountry || null);
  };

  return (
    <SelectPrimitive.Root
      value={value?.value || ""}
      onValueChange={handleSelect}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <SelectPrimitive.Trigger 
        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-atoll-500"
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
      >
        <SelectPrimitive.Value placeholder="Select a country">
          {value?.label || "Select a country"}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon>
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="relative z-50 min-w-[200px] w-[--radix-select-trigger-width] max-h-[300px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
          align="start"
        >
          <div 
            className="sticky top-0 z-10 bg-white p-2 border-b"
            onClick={handleSearchClick}
          >
            <input
              type="text"
              placeholder="Search country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="w-full p-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-atoll-500"
            />
          </div>

          <SelectPrimitive.Viewport className="p-1">
            {loading ? (
              <SelectPrimitive.Item
                value="loading"
                disabled
                className="relative flex items-center px-6 py-2 text-sm text-gray-500"
              >
                Loading countries...
              </SelectPrimitive.Item>
            ) : filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <SelectPrimitive.Item
                  key={country.value}
                  value={country.value}
                  className="relative flex items-center px-6 py-2 text-sm text-gray-900 rounded-md cursor-default select-none hover:bg-red-100 focus:bg-red-100 focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  <SelectPrimitive.ItemText>
                    {country.label}
                  </SelectPrimitive.ItemText>
                  <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <Check className="h-4 w-4 text-atoll-500" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                </SelectPrimitive.Item>
              ))
            ) : (
              <div className="px-6 py-2 text-sm text-gray-500">
                No countries found
              </div>
            )}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
};

export default CountrySelect;